import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  Box, Typography, Paper, Tabs, Tab, Grid, TextField, Button, Chip,
  CircularProgress, IconButton, Card, CardMedia, CardActions, Alert,
  Divider, MenuItem, List, ListItem, ListItemIcon, ListItemText, ListItemSecondaryAction
} from '@mui/material';
import {
  ArrowBack, Save, CloudUpload, Delete, Add, VideoLibrary,
  Image as ImageIcon, Description, InsertDriveFile, PlayCircle
} from '@mui/icons-material';

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

const SupplierProductDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const imageInputRef = useRef();
  const videoInputRef = useRef();
  const docInputRef = useRef();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState([]);
  const [catalogCodeTouched, setCatalogCodeTouched] = useState(false);

  // Form states
  const [basicInfo, setBasicInfo] = useState({
    name: '', nameAr: '', description: '', descriptionAr: '',
    shortDescription: '', category: '', supplierProductCode: '',
    basePrice: '', moq: '1', leadTime: '14',
    priceType: 'negotiable', isCustomManufacturing: false
  });
  const [specifications, setSpecifications] = useState([]);
  const [applications, setApplications] = useState([]);
  const [advantages, setAdvantages] = useState([]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    loadProduct();
    loadCategories();
  }, [id]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      setCategories(data.data || []);
    } catch (err) { console.error(err); }
  };

  const loadProduct = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products/supplier/${id}`);
      const p = data.data;
      setProduct(p);
      setCatalogCodeTouched(false);
      setBasicInfo({
        name: p.name || '', nameAr: p.nameAr || '',
        description: p.description || '', descriptionAr: p.descriptionAr || '',
        shortDescription: p.shortDescription || '',
        category: p.category?._id || p.category || '',
        supplierProductCode: p.supplierProductCode || '',
        basePrice: p.pricing?.basePrice?.toString() || '',
        moq: p.pricing?.moq?.toString() || '1',
        leadTime: p.shipping?.leadTime?.toString() || '14',
        priceType: p.pricing?.priceType || 'negotiable',
        isCustomManufacturing: p.isCustomManufacturing || false
      });
      setSpecifications(p.specifications || []);
      setApplications(p.applications || []);
      setAdvantages(p.advantages || []);
      setImages(p.images || []);
      setVideos(p.videos || []);
      setDocuments(p.documents || []);
    } catch (err) {
      setError('Failed to load product');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: basicInfo.name,
        nameAr: basicInfo.nameAr,
        description: basicInfo.description,
        descriptionAr: basicInfo.descriptionAr,
        shortDescription: basicInfo.shortDescription,
        isCustomManufacturing: basicInfo.isCustomManufacturing,
        pricing: {
          basePrice: Number(basicInfo.basePrice) || 0,
          moq: Number(basicInfo.moq) || 1,
          priceType: basicInfo.priceType
        },
        shipping: { leadTime: Number(basicInfo.leadTime) || 14 },
        specifications,
        applications: applications.filter(a => a.trim()),
        advantages: advantages.filter(a => a.trim()),
        images,
        videos,
        documents
      };

      if (catalogCodeTouched) {
        payload.supplierProductCode = basicInfo.supplierProductCode;
      }

      if (basicInfo.category) payload.category = basicInfo.category;

      const { data } = await api.put(`/products/${id}`, payload);
      const updatedProduct = data.data;

      setProduct(updatedProduct);
      setCatalogCodeTouched(false);
      setBasicInfo(prev => ({
        ...prev,
        supplierProductCode: updatedProduct.supplierProductCode || ''
      }));
      setSuccess('Product saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  };

  // Image upload
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    for (const file of files) {
      const formData = new FormData();
      formData.append('image', file);
      try {
        const { data } = await api.post('/upload/image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setImages(prev => [...prev, {
          url: data.data.url,
          alt: file.name.replace(/\.[^/.]+$/, ''),
          isPrimary: prev.length === 0,
          order: prev.length
        }]);
      } catch (err) { setError('Failed to upload image'); }
    }
    e.target.value = '';
  };

  // Video upload
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('video', file);
    try {
      const { data } = await api.post('/upload/video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setVideos(prev => [...prev, {
        url: data.data.url,
        title: file.name.replace(/\.[^/.]+$/, ''),
        thumbnail: ''
      }]);
    } catch (err) { setError('Failed to upload video'); }
    e.target.value = '';
  };

  // Document upload
  const handleDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('document', file);
    try {
      const { data } = await api.post('/upload/document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setDocuments(prev => [...prev, {
        name: file.name,
        url: data.data.url,
        type: 'catalog'
      }]);
    } catch (err) { setError('Failed to upload document'); }
    e.target.value = '';
  };

  const removeImage = (index) => setImages(prev => prev.filter((_, i) => i !== index));
  const removeVideo = (index) => setVideos(prev => prev.filter((_, i) => i !== index));
  const removeDoc = (index) => setDocuments(prev => prev.filter((_, i) => i !== index));
  const setPrimaryImage = (index) => setImages(prev => prev.map((img, i) => ({ ...img, isPrimary: i === index })));

  // Specifications helpers
  const addSpec = () => setSpecifications(prev => [...prev, { key: '', value: '', unit: '' }]);
  const updateSpec = (index, field, value) => setSpecifications(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  const removeSpec = (index) => setSpecifications(prev => prev.filter((_, i) => i !== index));

  // Applications/Advantages helpers
  const addApplication = () => setApplications(prev => [...prev, '']);
  const updateApplication = (index, value) => setApplications(prev => prev.map((a, i) => i === index ? value : a));
  const removeApplication = (index) => setApplications(prev => prev.filter((_, i) => i !== index));

  const addAdvantage = () => setAdvantages(prev => [...prev, '']);
  const updateAdvantage = (index, value) => setAdvantages(prev => prev.map((a, i) => i === index ? value : a));
  const removeAdvantage = (index) => setAdvantages(prev => prev.filter((_, i) => i !== index));

  if (loading) return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  if (!product) return <Typography color="error">Product not found</Typography>;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/supplier/products')}>Back</Button>
        <Box flex={1}>
          <Typography variant="h5" fontWeight={700}>{product.name}</Typography>
          <Box display="flex" gap={1} mt={0.5}>
            <Chip label={product.osesgCode} color="primary" size="small" />
            <Chip label={product.status} size="small" color={product.status === 'approved' ? 'success' : 'warning'} />
            {product.supplierProductCode && (
              <Chip label={`Your Code: ${product.supplierProductCode}`} size="small" variant="outlined" />
            )}
          </Box>
        </Box>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={18} /> : <Save />}
          onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable">
          <Tab label="Basic Info" />
          <Tab label="Images" icon={<ImageIcon />} iconPosition="start" />
          <Tab label="Videos" icon={<VideoLibrary />} iconPosition="start" />
          <Tab label="Documents" icon={<Description />} iconPosition="start" />
          <Tab label="Specifications" />
          <Tab label="Usage & Benefits" />
        </Tabs>
      </Paper>

      {/* Tab 0: Basic Info */}
      <TabPanel value={tab} index={0}>
        <Paper sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField fullWidth label="Product Name" value={basicInfo.name}
                onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="اسم المنتج (Arabic)" value={basicInfo.nameAr}
                onChange={(e) => setBasicInfo({ ...basicInfo, nameAr: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth select label="Category" value={basicInfo.category}
                onChange={(e) => setBasicInfo({ ...basicInfo, category: e.target.value })}>
                <MenuItem value="">-- Select --</MenuItem>
                {categories.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Your Catalog Code" value={basicInfo.supplierProductCode}
                onChange={(e) => {
                  setCatalogCodeTouched(true);
                  setBasicInfo({ ...basicInfo, supplierProductCode: e.target.value });
                }}
                helperText="Your internal reference number (not shown to customers)" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Short Description" value={basicInfo.shortDescription}
                onChange={(e) => setBasicInfo({ ...basicInfo, shortDescription: e.target.value })}
                helperText="Max 500 characters" inputProps={{ maxLength: 500 }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label="Full Description" value={basicInfo.description}
                onChange={(e) => setBasicInfo({ ...basicInfo, description: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label="الوصف الكامل (Arabic)" value={basicInfo.descriptionAr}
                onChange={(e) => setBasicInfo({ ...basicInfo, descriptionAr: e.target.value })} />
            </Grid>
            <Divider sx={{ width: '100%', my: 2 }} />
            <Grid item xs={4}>
              <TextField fullWidth select label="Price Type" value={basicInfo.priceType}
                onChange={(e) => setBasicInfo({ ...basicInfo, priceType: e.target.value })}>
                <MenuItem value="fixed">Fixed</MenuItem>
                <MenuItem value="range">Range</MenuItem>
                <MenuItem value="negotiable">Negotiable</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth label="Base Price ($)" type="number" value={basicInfo.basePrice}
                onChange={(e) => setBasicInfo({ ...basicInfo, basePrice: e.target.value })} />
            </Grid>
            <Grid item xs={2}>
              <TextField fullWidth label="MOQ" type="number" value={basicInfo.moq}
                onChange={(e) => setBasicInfo({ ...basicInfo, moq: e.target.value })} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth label="Lead Time (days)" type="number" value={basicInfo.leadTime}
                onChange={(e) => setBasicInfo({ ...basicInfo, leadTime: e.target.value })} />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>

      {/* Tab 1: Images */}
      <TabPanel value={tab} index={1}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Product Images (Multi-angle photos)</Typography>
            <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => imageInputRef.current.click()}>
              Upload Images
            </Button>
            <input type="file" ref={imageInputRef} hidden accept="image/*" multiple onChange={handleImageUpload} />
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Upload photos from different angles. Click the star to set the primary image.
          </Typography>
          {images.length === 0 ? (
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 6, textAlign: 'center', cursor: 'pointer' }}
              onClick={() => imageInputRef.current.click()}>
              <ImageIcon sx={{ fontSize: 60, color: '#ccc' }} />
              <Typography color="text.secondary">Click or drag images here</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {images.map((img, i) => (
                <Grid item xs={6} sm={4} md={3} key={i}>
                  <Card>
                    <CardMedia
                      component="div"
                      sx={{
                        height: 160,
                        bgcolor: '#f5f5f5',
                        backgroundImage: `url(${img.url})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        backgroundSize: 'contain'
                      }}
                      aria-label={img.alt || `Product image ${i + 1}`}
                      role="img"
                    />
                    <CardActions sx={{ justifyContent: 'space-between' }}>
                      <Button size="small" color={img.isPrimary ? 'primary' : 'inherit'}
                        onClick={() => setPrimaryImage(i)}>
                        {img.isPrimary ? '★ Primary' : '☆ Set Primary'}
                      </Button>
                      <IconButton size="small" color="error" onClick={() => removeImage(i)}><Delete /></IconButton>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </TabPanel>

      {/* Tab 2: Videos */}
      <TabPanel value={tab} index={2}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Operational Videos / فيديوهات تشغيلية</Typography>
            <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => videoInputRef.current.click()}>
              Upload Video
            </Button>
            <input type="file" ref={videoInputRef} hidden accept="video/*" onChange={handleVideoUpload} />
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Upload demonstration and operational videos showing the product in action.
          </Typography>
          {videos.length === 0 ? (
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 6, textAlign: 'center', cursor: 'pointer' }}
              onClick={() => videoInputRef.current.click()}>
              <PlayCircle sx={{ fontSize: 60, color: '#ccc' }} />
              <Typography color="text.secondary">Upload operational videos</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {videos.map((vid, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Card sx={{ p: 2 }}>
                    <Box sx={{ position: 'relative', bgcolor: '#000', borderRadius: 1, overflow: 'hidden', mb: 1 }}>
                      <video width="100%" height="200" controls>
                        <source src={vid.url} />
                      </video>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <TextField size="small" value={vid.title} label="Video Title"
                        onChange={(e) => setVideos(prev => prev.map((v, j) => j === i ? { ...v, title: e.target.value } : v))}
                        sx={{ flex: 1, mr: 1 }} />
                      <IconButton color="error" onClick={() => removeVideo(i)}><Delete /></IconButton>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      </TabPanel>

      {/* Tab 3: Documents */}
      <TabPanel value={tab} index={3}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Attachments & Documents / مرفقات</Typography>
            <Button variant="outlined" startIcon={<CloudUpload />} onClick={() => docInputRef.current.click()}>
              Upload Document
            </Button>
            <input type="file" ref={docInputRef} hidden accept=".pdf,.doc,.docx,.xls,.xlsx" onChange={handleDocUpload} />
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Upload catalogs, datasheets, manuals, and certificates.
          </Typography>
          {documents.length === 0 ? (
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 6, textAlign: 'center', cursor: 'pointer' }}
              onClick={() => docInputRef.current.click()}>
              <InsertDriveFile sx={{ fontSize: 60, color: '#ccc' }} />
              <Typography color="text.secondary">Upload product documents (PDF, DOC, XLS)</Typography>
            </Box>
          ) : (
            <List>
              {documents.map((doc, i) => (
                <ListItem key={i} divider>
                  <ListItemIcon><InsertDriveFile /></ListItemIcon>
                  <ListItemText
                    primary={doc.name}
                    secondary={
                      <TextField size="small" select value={doc.type} sx={{ mt: 0.5, minWidth: 150 }}
                        onChange={(e) => setDocuments(prev => prev.map((d, j) => j === i ? { ...d, type: e.target.value } : d))}>
                        <MenuItem value="catalog">Catalog</MenuItem>
                        <MenuItem value="datasheet">Datasheet</MenuItem>
                        <MenuItem value="manual">Manual</MenuItem>
                        <MenuItem value="certificate">Certificate</MenuItem>
                      </TextField>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton color="error" onClick={() => removeDoc(i)}><Delete /></IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </TabPanel>

      {/* Tab 4: Specifications */}
      <TabPanel value={tab} index={4}>
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Technical Specifications / المواصفات الفنية</Typography>
            <Button variant="outlined" startIcon={<Add />} onClick={addSpec}>Add Specification</Button>
          </Box>
          {specifications.map((spec, i) => (
            <Grid container spacing={2} key={i} sx={{ mb: 1 }}>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label="Property" value={spec.key}
                  onChange={(e) => updateSpec(i, 'key', e.target.value)} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" label="Value" value={spec.value}
                  onChange={(e) => updateSpec(i, 'value', e.target.value)} />
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth size="small" label="Unit" value={spec.unit}
                  onChange={(e) => updateSpec(i, 'unit', e.target.value)} />
              </Grid>
              <Grid item xs={1}>
                <IconButton color="error" onClick={() => removeSpec(i)}><Delete /></IconButton>
              </Grid>
            </Grid>
          ))}
          {specifications.length === 0 && (
            <Typography color="text.secondary" textAlign="center" py={3}>
              No specifications added yet. Click "Add Specification" to start.
            </Typography>
          )}
        </Paper>
      </TabPanel>

      {/* Tab 5: Usage & Benefits */}
      <TabPanel value={tab} index={5}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Applications / مجالات الاستخدام</Typography>
            <Button variant="outlined" startIcon={<Add />} onClick={addApplication}>Add</Button>
          </Box>
          {applications.map((app, i) => (
            <Box key={i} display="flex" gap={1} mb={1}>
              <TextField fullWidth size="small" value={app} placeholder="e.g. Metal fabrication, Warehouse operations..."
                onChange={(e) => updateApplication(i, e.target.value)} />
              <IconButton color="error" onClick={() => removeApplication(i)}><Delete /></IconButton>
            </Box>
          ))}
          {applications.length === 0 && (
            <Typography color="text.secondary" textAlign="center" py={2}>
              Describe where and how this product is used.
            </Typography>
          )}
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Advantages / المزايا</Typography>
            <Button variant="outlined" startIcon={<Add />} onClick={addAdvantage}>Add</Button>
          </Box>
          {advantages.map((adv, i) => (
            <Box key={i} display="flex" gap={1} mb={1}>
              <TextField fullWidth size="small" value={adv} placeholder="e.g. High precision, Low maintenance..."
                onChange={(e) => updateAdvantage(i, e.target.value)} />
              <IconButton color="error" onClick={() => removeAdvantage(i)}><Delete /></IconButton>
            </Box>
          ))}
          {advantages.length === 0 && (
            <Typography color="text.secondary" textAlign="center" py={2}>
              List the key advantages and benefits of this product.
            </Typography>
          )}
        </Paper>
      </TabPanel>
    </Box>
  );
};

export default SupplierProductDetail;
